import React, { useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import KeyboardArrowDownRounded from '@mui/icons-material/KeyboardArrowDownRounded';
import LocationOnRounded from '@mui/icons-material/LocationOnRounded';
import MyLocationRounded from '@mui/icons-material/MyLocationRounded';

import Button from './Button';
import LocationPickerModal from './LocationPickerModal';
import { formatAddressLine } from '../services/locationResolve';

function storeNameFromCache(cache) {
  return cache?.storeName ?? cache?.store?.storeName ?? null;
}

/** Primary line + optional subtitle for the banner. */
function addressLabel(cache) {
  const addr = cache?.customerAddress;
  if (!addr) return null;
  const tag = addr.addressType || addr.type || addr.label || addr.tag;
  if (tag) return String(tag).trim().toUpperCase();
  return 'HOME';
}

function bannerLines(cache) {
  if (!cache) return { primary: null, subtitle: null, deliverLabel: null };

  const deliverLabel = addressLabel(cache);
  const storeName = storeNameFromCache(cache);
  const addrLine = formatAddressLine(cache.customerAddress);

  if (addrLine) {
    return { primary: addrLine, subtitle: storeName, deliverLabel };
  }

  const areaName = cache.areaName || null;
  if (areaName && storeName) {
    return { primary: areaName, subtitle: storeName, deliverLabel };
  }
  if (areaName) return { primary: areaName, subtitle: null, deliverLabel };
  if (storeName) return { primary: storeName, subtitle: null, deliverLabel };

  return { primary: null, subtitle: null, deliverLabel };
}

export default function LocationBanner({
  cache,
  resolving = false,
  error = null,
  permission = null,
  onDetect,
  onLocationPicked,
  addresses = [],
}) {
  const [pickerOpen, setPickerOpen] = useState(false);

  const { primary, subtitle, deliverLabel } = bannerLines(cache);
  const hasStore = Boolean(cache?.storeId);
  const needsPermission = !resolving && (permission === 'prompt' || permission === 'denied');
  const showContent = !resolving && (primary || needsPermission);

  const onUseMyLocation = (e) => {
    e?.stopPropagation();
    onDetect?.();
  };

  const openPicker = (e) => {
    if (e?.target?.closest?.('button')) return;
    setPickerOpen(true);
  };

  return (
    <>
      <Box
        onClick={openPicker}
        role="button"
        aria-label="Change delivery location"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          width: '100%',
          px: 0,
          py: 1.25,
          mb: 0,
          bgcolor: 'transparent',
          border: 'none',
          borderRadius: 0,
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            bgcolor: '#F5D9D5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <LocationOnRounded sx={{ color: 'primary.main', fontSize: 22 }} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontWeight: 600,
              lineHeight: 1,
              display: 'block',
              mb: 0.35,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              fontSize: '0.65rem',
            }}
          >
            {deliverLabel ? `Delivering to · ${deliverLabel}` : 'Delivering to'}
          </Typography>

          {resolving && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <CircularProgress size={12} thickness={5} />
              <Typography variant="body2" color="text.secondary">
                Locating…
              </Typography>
            </Box>
          )}

          {showContent && primary && (
            <>
              <Typography
                variant="subtitle1"
                noWrap
                sx={{
                  lineHeight: 1.25,
                  fontFamily: '"Foregen", "Roboto Slab", Georgia, serif',
                  fontSize: '0.95rem',
                }}
              >
                {primary}
              </Typography>
              {subtitle && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  sx={{ display: 'block', lineHeight: 1.3 }}
                >
                  {subtitle}
                </Typography>
              )}
            </>
          )}

          {showContent && needsPermission && !primary && (
            <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.25, color: 'text.secondary' }}>
              Set your location
            </Typography>
          )}

          {!resolving && error && (
            <Typography variant="caption" color="error" display="block">
              {error}
            </Typography>
          )}

          {!resolving && !error && permission === 'granted' && !hasStore && primary && (
            <Typography variant="caption" color="warning.main" display="block">
              Store unavailable for this location
            </Typography>
          )}
        </Box>

        {needsPermission ? (
          <Button
            size="small"
            startIcon={<MyLocationRounded sx={{ fontSize: 14 }} />}
            sx={{ flexShrink: 0, fontSize: 12, fontWeight: 600, py: 0.5, px: 1.25, borderRadius: 5 }}
            onClick={onUseMyLocation}
          >
            Detect
          </Button>
        ) : (
          <KeyboardArrowDownRounded sx={{ color: 'text.secondary', fontSize: 22, flexShrink: 0 }} />
        )}
      </Box>

      <LocationPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        addresses={addresses}
        onResolved={onLocationPicked}
      />
    </>
  );
}
